import React, { useEffect, useState, useMemo } from "react";
import Plot from "react-plotly.js";

/**
 * BetweennessBarChart
 * ───────────────────
 * Per-residue betweenness centrality. Single bar per residue, colored by
 * hub status: dark for top-N% hubs, light for the rest. Unlike the
 * "contacts" charts, betweenness is a single scalar per residue and does
 * not decompose into base + highlight values, so a single colored trace
 * is the honest visualization.
 *
 * Props
 *   data            Array<{ position, betweenness, isHub?, degree? }>
 *   percentile      Number (default 10) — used to derive isHub if absent
 *   title           Optional title override
 *   onResidueClick  (position) => void
 */
const BetweennessBarChart = ({
    data,
    percentile = 10,
    title = "",
    onResidueClick,
}) => {
    const [layout, setLayout] = useState({});

    const { positions, values, colors, hubCount } = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0) {
            return { positions: [], values: [], colors: [], hubCount: 0 };
        }

        // Sort by numeric residue position when possible
        const sorted = [...data].sort((a, b) => {
            const na = parseInt(String(a.position).split("_").pop(), 10);
            const nb = parseInt(String(b.position).split("_").pop(), 10);
            if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
            return String(a.position).localeCompare(String(b.position));
        });

        // Derive isHub if not provided (top-percentile by betweenness)
        const anyExplicit = sorted.some((d) => typeof d.isHub === "boolean");
        let flags;
        if (anyExplicit) {
            flags = sorted.map((d) => !!d.isHub);
        } else {
            const vsorted = [...sorted]
                .map((d) => d.betweenness || 0)
                .sort((a, b) => b - a);
            const cutoffIdx = Math.max(
                1,
                Math.floor((vsorted.length * percentile) / 100)
            );
            const threshold =
                vsorted[Math.min(cutoffIdx - 1, vsorted.length - 1)];
            flags = sorted.map((d) => (d.betweenness || 0) >= threshold);
        }

        return {
            positions: sorted.map((d) => d.position),
            values: sorted.map((d) => d.betweenness || 0),
            colors: flags.map((isHub) => (isHub ? "#e08e2a" : "#f0c48a")),
            hubCount: flags.filter(Boolean).length,
        };
    }, [data, percentile]);

    const plotData = useMemo(() => {
        if (positions.length === 0) return [];

        return [
            {
                x: positions,
                y: values,
                type: "bar",
                name: "Betweenness",
                marker: { color: colors, line: { width: 0 } },
                hovertemplate:
                    "Residue %{x}<br>Betweenness: %{y:.2f}<extra></extra>",
                showlegend: false,
            },
            // Dummy invisible traces just to populate the legend with two entries
            {
                x: [null],
                y: [null],
                type: "bar",
                name: `Top ${percentile}% hub`,
                marker: { color: "#e08e2a" },
                showlegend: true,
            },
            {
                x: [null],
                y: [null],
                type: "bar",
                name: "Other",
                marker: { color: "#f0c48a" },
                showlegend: true,
            },
        ];
    }, [positions, values, colors, percentile]);

    useEffect(() => {
        setLayout({
            autosize: true,
            title: { text: title },
            xaxis: { type: "category", title: { text: "Residue position" },  tickangle: -90,
            automargin: true,
            tickfont: { size: 9 } },
            yaxis: { title: { text: "Betweenness" } },
            margin: { t: 50, b: 120, l: 60, r: 30 },
            showlegend: true,
            legend: { orientation: "h", y: 1.12, x: 1, xanchor: "right" },
            bargap: 0.15,
        });
    }, [title]);

    const handleClick = (ev) => {
        if (!onResidueClick) return;
        const pt = ev?.points?.[0];
        if (pt && pt.x) onResidueClick(pt.x);
    };

    return (
        <div className="aspect-square ">
            <Plot
                data={plotData}
                layout={layout}
                useResizeHandler
                onClick={handleClick}
                style={{ width: "100%", height: "100%" }}
                config={{ displaylogo: false, responsive: true }}
            />
            {hubCount > 0 && (
                <div className="px-3 pb-2 text-secondary small">
                    {hubCount} hub{hubCount === 1 ? "" : "s"} highlighted.
                </div>
            )}
        </div>
    );
};

export default BetweennessBarChart;